import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContractService from '~/services/modules/contract';
import useStore from '~/store';
import { parseParamToString } from '~/utils/parseParamToString';

export interface Club {
  club_name: string;
  club_value: number;
  club_description: string;
  type_club: number;
  covers: any[];
}

export interface Distributor {
  name: string;
}

export interface Seller {
  id: string;
  name: string;
}

export interface StepsResponse {
  steps: number[];
  club: Club;
  distributor?: Distributor | null;
  has_promotion?: boolean;
  is_promotion_available?: boolean;
  organization_id?: string | null;
  seller?: Seller | string | null;
}

export interface ErrorInfo {
  statusCode?: number;
  message?: string;
}

export const useGetStepPlans = () => {
  const [data, setData] = useState<StepsResponse | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    plan_id,
    setPaymentPlanData,
    setClubData,
    setDistributorId,
    setOrganizationId,
    setSeller,
    setPromotionData
  } = useStore((store) => ({
    plan_id: store.quote.plan_id,
    setPaymentPlanData: store.setPayment,
    setClubData: store.setClub,
    setDistributorId: store.setDistributorId,
    setOrganizationId: store.setOrganizationId,
    setSeller: store.setSeller,
    setPromotionData: store.setPromotionData
  }));

  const fetchData = async () => {
    try {
      // Zerar estado derivado do flow ao entrar no app para evitar dados de sessão anterior (ex.: fluxo de pagamento)
      setPaymentPlanData({ membership_fee: { methods: [] }, installments: { methods: [] } });
      setClubData({
        club_name: '',
        club_value: 0,
        club_description: '',
        type_club: 0,
        covers: []
      });
      setDistributorId(null);
      setOrganizationId(null);
      setSeller(null);
      setPromotionData({});

      const urlDistributorId = searchParams.get('dis');
      const urlChannelId = searchParams.get('channel');
      const distributorId = urlDistributorId;

      console.log('Channel ID:', urlChannelId);

      if (distributorId) {
        setDistributorId(distributorId);
      }

      const orgId = searchParams.get('org') || undefined;
      const sellerId = searchParams.get('seller') || undefined;
      if (orgId) setOrganizationId(orgId);
      if (sellerId) setSeller(sellerId);

      const urlPlanId = searchParams.get('plan');
      const planId = urlPlanId || parseParamToString(plan_id) || '';

      let responseData;
      const hasDistributor = Boolean(distributorId);
      const distributorCode = distributorId ?? '';
      const channelParam = urlChannelId ?? '';

      if (hasDistributor) {
        responseData = await ContractService.getStepPlansWithChannel(
          planId,
          distributorCode,
          channelParam || undefined
        );
        const flowData = responseData.data ?? responseData;
        const organizationIdFromFlow = flowData.organization_id ?? '';

        const newParams: Record<string, string> = {
          plan: planId,
          dis: distributorCode,
          org: organizationIdFromFlow
        };
        if (channelParam) newParams.channel = channelParam;
        const modalParam = searchParams.get('modal');
        if (modalParam) newParams.modal = modalParam;
        if (organizationIdFromFlow) setOrganizationId(organizationIdFromFlow);
        setSearchParams(newParams, { replace: true });
        // Fluxo com dis: usa apenas o flow de distribuidor, não chama getStepPlans (flow plan)
      } else {
        responseData = await ContractService.getStepPlans(planId, orgId, sellerId);
        const flowData = responseData.data ?? responseData;
        const organizationIdFromFlow = flowData.organization_id ?? '';

        const newParams: Record<string, string> = {
          plan: planId,
          org: organizationIdFromFlow
        };
        if (sellerId) newParams.seller = sellerId;
        if (channelParam) newParams.channel = channelParam;
        const modalParam = searchParams.get('modal');
        if (modalParam) newParams.modal = modalParam;
        if (organizationIdFromFlow) setOrganizationId(organizationIdFromFlow);
        setSearchParams(newParams, { replace: true });
      }

      const dataPayload = responseData.data ?? responseData;
      setData(dataPayload);
      setPaymentPlanData(dataPayload.payment);
      setClubData(dataPayload.club);

      if (dataPayload.has_promotion !== undefined || dataPayload.is_promotion_available !== undefined) {
        setPromotionData({
          has_promotion: dataPayload.has_promotion,
          is_promotion_available: dataPayload.is_promotion_available
        });
      }
    } catch (error: any) {
      let errorInfo: ErrorInfo = {
        statusCode: 500,
        message: 'Erro desconhecido'
      };

      if (error?.response) {
        errorInfo.statusCode = error.response.status;

        // Tentar extrair a mensagem do backend
        const responseData = error.response.data;
        if (responseData?.notifications && responseData.notifications.length > 0) {
          errorInfo.message = responseData.notifications[0].message;
        } else if (responseData?.data) {
          errorInfo.message = responseData.data;
        } else {
          errorInfo.message = error.message || 'Erro na requisição';
        }
      } else if (error?.message) {
        errorInfo.message = error.message;
      }

      setError(errorInfo);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { data, loading, error };
};
